import { GeoPoint, Timestamp } from "firebase/firestore";
import { PaymentType } from "./PaymentType";
import { PlaceDataSubmissionStatus } from "./PlaceDataSubmissionStatus";
import { PlaceStatus } from "./PlaceStatus";
import { UserProfile } from "./User";
import { UserAttributableText } from "./UserAttributableText";
import { VibeType } from "./VibeType";

export interface PlaceDocument {
  id?: string;
  googlePlaceId: string;
  name: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  listerUserId?: string;
  // Location
  location?: GeoPoint;
  address: string;
  geohash?: string;
  // Business and Contact Info
  website?: string;
  status?: PlaceStatus;
  // Metadata
  numberOfTables?: number;
  vibes?: VibeType[];
  vibeSummary?: UserAttributableText;
  paymentTypes?: PaymentType[];
  // Activity
  checkedInUserIds?: string[];
  likes?: number;
  checkInHistory?: Record<string, Timestamp>;
}

export interface Place {
  // We mostly use the Google Place ID as the ID, but we keep the id field for future compatibility.
  id?: string;
  googlePlaceId: string;
  name: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  listerUserId?: string;
  // Location
  address: string;
  lat: number;
  lng: number;
  // Business and Contact Info
  website?: string;
  status?: PlaceStatus;
  // Metadata
  numberOfTables: number;
  vibes?: VibeType[];
  vibeSummary?: UserAttributableText;
  paymentTypes?: PaymentType[];
  // Activity
  checkedInUserIds: string[];
  likes?: number;
  checkInHistory?: Record<string, Timestamp>;
}

// Collection: "placeDataSubmissions"
export interface PlaceDataSubmissionDocument {
  id: string;
  createdAt: Timestamp;
  placeId: string;
  creatorId: string;
  // Verification status
  submissionStatus: PlaceDataSubmissionStatus;
  confirmedUserIds: string[];
  rejectedUserIds: string[];
  flaggedUserIds: string[];
  // Information the user can provide:
  numberOfTables?: number;
  vibes?: VibeType[];
  vibeSummary?: UserAttributableText;
  paymentTypes?: PaymentType[];
  status?: PlaceStatus;
}

export interface PlaceDataSubmission {
  id: string;
  placeId: string;
  createdAt: Timestamp;
  creatorId: string;
  // Verification status
  submissionStatus: PlaceDataSubmissionStatus;
  confirmedUserIds: string[];
  rejectedUserIds: string[];
  flaggedUserIds: string[];
  // Information the user can provide:
  numberOfTables?: number;
  vibes?: VibeType[];
  vibeSummary?: UserAttributableText;
  paymentTypes?: PaymentType[];
  status?: PlaceStatus;
}

export interface ResolvedPlaceDataSubmission extends PlaceDataSubmission {
  place: Place | null;
  creator: UserProfile | null;
}

export function mapPlaceDocumentToPlace(data: PlaceDocument): Place {
  const place: Place = {
    ...data,
    lat: data.location ? data.location.latitude : 0,
    lng: data.location ? data.location.longitude : 0,
    numberOfTables: data.numberOfTables ?? 0,
    checkedInUserIds: data.checkedInUserIds ?? [],
    checkInHistory: data.checkInHistory ?? {},
  };
  return place;
}
